import { z } from "zod";

export const invoiceRequestSchema = z.object({
	id: z.string().optional().nullable(),
	requested_by: z.string().optional().nullable(),
	requested_by_user: z
		.object({
			id: z.string(),
			name: z.string(),
			email: z.string(),
		})
		.optional()
		.nullable(),
	request_date: z.string().min(1, "Request date is required"),
	client_id: z.string().optional().nullable(),
	client: z
		.object({
			id: z.string(),
			name: z.string(),
			company: z.string().optional().nullable(),
		})
		.optional()
		.nullable(),
	client_name: z.string().optional().nullable(),
	client_address: z.string().min(1, "Client address is required"),
	tin_no: z.string().min(1, "TIN No. is required"),
	project_id: z.string().optional().nullable(),
	project: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.optional()
		.nullable(),
	sales_contract_id: z.string().min(1, "Sales contract is required"),
	sales_contract: z
		.object({
			id: z.string(),
			project_name: z.string(),
			contract_amount: z.string().optional().nullable(),
			status: z.string(),
		})
		.optional()
		.nullable(),
	invoice_id: z.string().optional().nullable(),
	invoice: z
		.object({
			id: z.string(),
			invoice_number: z.string(),
			amount: z.string().optional().nullable(),
			status: z.string(),
			invoice_date: z.string().optional().nullable(),
			due_date: z.string().optional().nullable(),
			payment_method: z.string().optional().nullable(),
			cancelled_at: z.string().nullable().optional(),
			cancellation_reason: z.string().nullable().optional(),
			is_cancelled: z.boolean().optional(),
			ar_journal_entry_status: z.string().nullable().optional(),
		})
		.optional()
		.nullable(),
	project_name: z.string().optional().nullable(),
	is_under_project: z.boolean().default(false),
	invoice_type: z.enum(["partial", "progress", "final"]).optional().nullable(),
	billing_type: z.enum(["one_time", "recurring", "mixed"]),
	amount_one_time: z.coerce.number().min(0).optional().nullable(),
	amount_recurring: z.coerce.number().min(0).optional().nullable(),
	amount_mixed: z.coerce.number().min(0).optional().nullable(),
	vatable_sales: z.coerce.number().min(0).optional().nullable(),
	vat_amount: z.coerce.number().min(0).optional().nullable(),
	total_amount_due: z.coerce.number().min(0).optional().nullable(),
	payment_terms: z.string().optional().nullable(),
	contract_duration_months: z.coerce.number().int().min(1).optional().nullable(),
	invoice_date_requested: z.string().min(1, "Invoice date requested is required"),
	contact_person: z.string().min(1, "Contact person is required"),
	contact_number: z.string().min(1, "Contact number is required"),
	contact_email: z.string().email("Valid email is required").min(1, "Contact email is required"),
	status: z.enum(["pending", "approved", "processed", "rejected"]).default("pending"),
	processed_at: z.string().optional().nullable(),
	payment_due_date: z.string().optional().nullable(),
	payment_status: z.string().optional().nullable(),
	payment_received_at: z.string().optional().nullable(),
	payment_amount: z.coerce.number().min(0).optional().nullable(),
	payment_date: z.string().optional().nullable(),
	payment_reference_number: z.string().optional().nullable(),
	payment_method: z.string().optional().nullable(),
	receipt_number: z.string().optional().nullable(),
	deduction_withholding_tax: z.coerce.number().min(0).optional().nullable(),
	deduction_liquidated_damages: z.coerce.number().min(0).optional().nullable(),
	deduction_retention: z.coerce.number().min(0).optional().nullable(),
	deduction_municipal_charge: z.coerce.number().min(0).optional().nullable(),
	deduction_bank_charge: z.coerce.number().min(0).optional().nullable(),
	net_amount: z.coerce.number().min(0).optional().nullable(),
	payment_attachment_url: z.string().optional().nullable(),
	payment_attachments: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
				url: z.string(),
				size: z.number(),
				mime_type: z.string(),
			}),
		)
		.optional()
		.nullable(),
	items: z
		.array(
			z.object({
				id: z.string().optional().nullable(),
				quantity: z.coerce.number().min(0),
				unit_of_measurement: z.string(),
				item_description: z.string(),
				unit_price: z.coerce.number().min(0),
				total_price: z.coerce.number().min(0),
				sort_order: z.number().optional(),
			}),
		)
		.optional()
		.nullable(),
	follow_up_notes: z.string().optional().nullable(),
	notes: z.string().optional().nullable(),
	created_at: z.string().optional().nullable(),
	updated_at: z.string().optional().nullable(),
	sent_billed_at: z.string().optional().nullable(),
	delivery_method: z.enum(["email", "courier", "in_person", "other"]).optional().nullable(),
	recipient_notes: z.string().optional().nullable(),
	sent_billed_by_user: z
		.object({
			id: z.string(),
			name: z.string(),
			email: z.string(),
		})
		.optional()
		.nullable(),

});

export type InvoiceRequest = z.infer<typeof invoiceRequestSchema>;

export const createInvoiceRequestSchema = invoiceRequestSchema.omit({
	id: true,
	requested_by_user: true,
	client: true,
	project: true,
	sales_contract: true,
	invoice: true,
	created_at: true,
	updated_at: true,
	sent_billed_by_user: true,

});

export type CreateInvoiceRequestData = z.infer<typeof createInvoiceRequestSchema>;

export const updateInvoiceRequestSchema = createInvoiceRequestSchema.partial().extend({
	id: z.string(),
});

export type UpdateInvoiceRequestData = z.infer<typeof updateInvoiceRequestSchema>;

export interface InvoiceRequestFilters {
	"filter[search]"?: string;
	"filter[status]"?: string;
	"filter[billing_type]"?: string;
	per_page?: number;
	page?: number;
}
